import { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { FiAlertCircle } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import apiV2 from "api-v2";
import type { Company } from "newui/features/Company/types";

type LeadScoreVisibility = "always" | "only_when_not_scoring" | "only_after_all_submitted";

interface EvaluationPermissionsProps {
	company: Company;
}

const VISIBILITY_OPTIONS: {
	value: LeadScoreVisibility;
	label: string;
	description: string;
}[] = [
	{
		value: "always",
		label: "Always",
		description:
			"The RFx Lead can view individual panel member scores at any time, including while they are scoring.",
	},
	{
		value: "only_when_not_scoring",
		label: "Only when not scoring",
		description:
			"The RFx Lead can only view panel member scores when they are NOT participating as a scorer. This prevents bias from seeing other scores before submitting their own.",
	},
	{
		value: "only_after_all_submitted",
		label: "Only after all submitted",
		description:
			"The RFx Lead can only view individual panel member scores after all active panel members have submitted their evaluations.",
	},
];

export const EvaluationPermissions: React.FC<EvaluationPermissionsProps> = ({
	company,
}) => {
	const toast = useToast();
	const [visibility, setVisibility] = useState<LeadScoreVisibility>("always");
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchSettings = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await apiV2.get("/company/evaluation-settings");
			const data = (response.data as any)?.data ?? response.data;
			setVisibility(data.lead_score_visibility ?? "always");
		} catch {
			setError("Failed to load evaluation settings.");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchSettings();
	}, [fetchSettings]);

	const handleChange = useCallback(
		async (value: LeadScoreVisibility) => {
			const previousValue = visibility;
			setVisibility(value);
			setIsSaving(true);
			try {
				await apiV2.patch("/company/evaluation-settings", {
					lead_score_visibility: value,
				});
				toast({
					title: "Settings updated",
					description: "Evaluation permission settings have been saved.",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
			} catch {
				setVisibility(previousValue);
				toast({
					title: "Error",
					description: "Failed to update settings. Please try again.",
					status: "error",
					duration: 4000,
					isClosable: true,
				});
			} finally {
				setIsSaving(false);
			}
		},
		[visibility, toast]
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner size="lg" color="blue.500" />
			</div>
		);
	}

	return (
		<div data-testid="evaluation-permissions">
			<div className="mb-6">
				<h3 className="text-lg font-semibold text-gray-900">
					Allow RFx Lead to View Scores
				</h3>
				<p className="mt-1 text-sm text-gray-500">
					Control when the RFx Lead can view individual panel member scores
					during an evaluation. This setting applies as the default for all new
					evaluations created by {company?.name ?? "your company"}.
				</p>
			</div>

			{error && (
				<div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
					<FiAlertCircle className="shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<div className="space-y-3">
				{VISIBILITY_OPTIONS.map((option) => (
					<label
						key={option.value}
						className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
							visibility === option.value
								? "border-[#0031DD] bg-[#e8eeff]"
								: "border-gray-200 bg-white hover:bg-gray-50"
						} ${isSaving ? "opacity-60 cursor-not-allowed" : ""}`}
						data-testid={`visibility-option-${option.value}`}
					>
						<input
							type="radio"
							name="lead_score_visibility"
							value={option.value}
							checked={visibility === option.value}
							onChange={() => handleChange(option.value)}
							disabled={isSaving}
							className="mt-1 h-4 w-4 text-[#0031DD] focus:ring-[#0031DD]"
						/>
						<div>
							<p className="text-sm font-medium text-gray-900">
								{option.label}
							</p>
							<p className="mt-0.5 text-xs text-gray-500">
								{option.description}
							</p>
						</div>
					</label>
				))}
			</div>
		</div>
	);
};
